import React from "react";
import { Tile, Block, Pills } from "./ViewUI";
import Lottie from "lottie-react";

import iconSourceFunds from "../../../images/SoF.json";
import iconSourceWealth from "../../../images/SoW.json";
import iconPaymentRoutes from "../../../images/transfer2-lottie.json";

import iconNetWorth from "../../../images/piggy-bank.svg";
import iconAnnualIncome from "../../../images/managementX.svg";

const SOW_OPTIONS = [
  { label: "Employment", value: "employment" },
  { label: "Self-employed", value: "self_employed" },
  { label: "Business owner", value: "business_owner" },
  { label: "Sale of company", value: "sale_business" },
  { label: "Real estate sale", value: "sale_real_estate" },
  { label: "Rental income", value: "rental_income" },
  { label: "Investment returns", value: "investment_returns" },
  { label: "Inheritance", value: "inheritance" },
  { label: "Gift", value: "gift" },
  { label: "Legal settlement", value: "legal_settlement" },
  { label: "Divorce settlement", value: "divorce_settlement" },
  { label: "Virtual asset gains", value: "virtual_asset_gains" },
  { label: "Other", value: "other" },
];

const SOF_OPTIONS = [
  { label: "Employment income", value: "employment_income" },
  { label: "Professional income", value: "professional_income" },
  { label: "Business income", value: "business_income" },
  { label: "Sale of business proceeds", value: "sale_business_proceeds" },
  { label: "Sale of property proceeds", value: "sale_property_proceeds" },
  { label: "Rental income", value: "rental_income_sof" },
  { label: "Sale of securities", value: "sale_securities" },
  { label: "Dividends distribution", value: "dividends_distribution" },
  { label: "Inheritance proceeds", value: "inheritance_proceeds" },
  { label: "Gift proceeds", value: "gift_proceeds" },
  { label: "Legal settlement proceeds", value: "legal_settlement_proceeds" },
  { label: "Divorce settlement proceeds", value: "divorce_settlement_proceeds" },
  { label: "Sale of virtual assets", value: "sale_virtual_assets" },
  { label: "Loan", value: "loan" },
  { label: "Other", value: "other" },
];

const PAYMENT_ROUTES_OPTIONS = [
  { label: "Transfer from own account", value: "transfer_own_account" },
  { label: "Third-party payment", value: "third_party_payment" },
  { label: "Cash deposit", value: "cash_deposit" },
  { label: "Cheque", value: "cheque" },
  { label: "Card payment", value: "card_payment" },
  { label: "Virtual assets transfer", value: "virtual_assets_transfer" },
  { label: "Other", value: "other" },
];

const SOW_LABEL_MAP = Object.fromEntries(
  SOW_OPTIONS.map((opt) => [opt.value, opt.label])
);

const SOF_LABEL_MAP = Object.fromEntries(
  SOF_OPTIONS.map((opt) => [opt.value, opt.label])
);

const PAYMENT_ROUTES_LABEL_MAP = Object.fromEntries(
  PAYMENT_ROUTES_OPTIONS.map((opt) => [opt.value, opt.label])
);

function mapValuesToLabels(values, labelMap) {
  return Array.isArray(values)
    ? values.map((v) => labelMap[v] || v).filter(Boolean)
    : [];
}

function formatRangeWithCurrency(range, currency, emptyLabel = "-") {
  const rangeText = String(range || "").trim();
  const currencyText = String(currency || "").trim();

  if (!rangeText) return emptyLabel;
  return [rangeText, currencyText].filter(Boolean).join(" ");
}

export default function KycViewSowSof({ sectionNo = 0, form }) {
  const s = form?.sourceOfFundsWealth || {};
  const customerType = String(
    form?.customer?.type ||
      form?.customer?.clientType ||
      form?.customer?.customerType ||
      ""
  )
    .trim()
    .toLowerCase();
  const isEntityClient =
    customerType === "entity" ||
    customerType === "company" ||
    customerType === "corporate";
  const ownership = form?.ownership || {};
  const ownershipProfile = String(ownership?.desktopClientEntityType || "")
    .trim()
    .toLowerCase();
  const ownershipHoldingKind = String(ownership?.desktopClientHoldingKind || "")
    .trim()
    .toLowerCase();
  const ownershipMode = String(
    ownership?.mobilePreliminaryMode ||
      ownership?.assistantPreliminaryMode ||
      ownership?.mobileAssistantDraft?.preliminaryMode ||
      ownership?.assistantTree?.initialAHType ||
      ownership?.preliminaryMode ||
      ""
  )
    .trim()
    .toLowerCase();
  const showsCompanyTurnover =
    isEntityClient &&
    (ownershipProfile === "operating" ||
      (ownershipProfile === "holding" && ownershipHoldingKind === "active") ||
      ownership?.preliminaryNoneOfAbove === true ||
      ownershipMode === "noneofabove" ||
      ownershipMode === "holdingactive");

  const sowTypesLabels = mapValuesToLabels(s?.sourceOfWealth, SOW_LABEL_MAP);
  const sofTypesLabels = mapValuesToLabels(s?.sourceOfFundsTypes, SOF_LABEL_MAP);
  const paymentRoutesLabels = mapValuesToLabels(s?.paymentRoutes, PAYMENT_ROUTES_LABEL_MAP);

  const hasSowOtherDetails =
    Array.isArray(s?.sourceOfWealth) &&
    s.sourceOfWealth.includes("other") &&
    !!s?.sourceOfWealthDetails;

  const hasSofLoanDetails =
    Array.isArray(s?.sourceOfFundsTypes) &&
    s.sourceOfFundsTypes.includes("loan") &&
    !!s?.sourceOfFundsLoanDetails;

  const hasSofOtherDetails =
    Array.isArray(s?.sourceOfFundsTypes) &&
    s.sourceOfFundsTypes.includes("other") &&
    !!s?.sourceOfFundsOtherDetails;

  const hasPaymentRoutesThirdPartyDetails =
    Array.isArray(s?.paymentRoutes) &&
    s.paymentRoutes.includes("third_party_payment") &&
    !!s?.paymentRoutesThirdPartyDetails;

  const hasPaymentRoutesOtherDetails =
    Array.isArray(s?.paymentRoutes) &&
    s.paymentRoutes.includes("other") &&
    !!s?.paymentRoutesOtherDetails;

  return (
    <div className="card kycCard">
      <div className={"kycSectionHead kycSectionHeadSection" + sectionNo}>
        <h3 className="kycSectionTitle">
          {sectionNo ? `${sectionNo} | ` : ""}Source of funds & wealth
        </h3>
      </div>

      <div className="kycSectionBody">
        <div className="row g-3">
          {/* ---- */}
          {showsCompanyTurnover ? (
            <>
              <div className="col-12">
                <div className="kycViewTile kycSowSofTile">
                  <div className="kycInfoItem">
                    <div className="kycInfoIcon">
                      <img src={iconAnnualIncome} alt="" className="kycInfoIconImg" />
                    </div>
                    <div className="kycInfoContent">
                      <div className="kycInfoViewLabelSoWSoF">Company annual turnover</div>
                      <div className="kycInfoViewText">
                        {formatRangeWithCurrency(s?.annualTurnoverRange, s?.annualTurnoverCurrency)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : null}

          {/* Row 1: Source of wealth + Net worth/Annual income */}
          <div className="col-12">
            <div className="kycViewTile kycSowSofTile">
              <div className="kycInfoItem kycInfoItemAlignTop">
                <div className="kycInfoIconSoWSoF">
                  <Lottie animationData={iconSourceWealth} loop autoplay />
                </div>

                <div className="kycInfoContent w-100">
                  <div className="kycInfoViewLabelSoWSoF">Source of wealth</div>

                  {sowTypesLabels.length ? (
                    <div className="kycViewPills">
                      {sowTypesLabels.map((item, i) => (
                        <span key={i} className="kycViewPillSoWSoF">
                          {item}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <div className="kycInfoViewText">—</div>
                  )}

                  {hasSowOtherDetails ? (
                    <div className="kycInlineDetailWrap kycInlineDetailWrapFull" style={{ marginTop: 10 }}>
                      <div className="kycInfoViewLabel kycInlineDetailLabel">
                        Other (details)
                      </div>
                      <div className="kycInfoViewTextDetails">
                        {s.sourceOfWealthDetails}
                      </div>
                    </div>
                  ) : null}

                  <hr style={{ margin: "12px 0", borderColor: "rgba(0,0,0,0.25)" }} />

                  <div className="row g-3">
                    <div className="col-12 col-lg-6">
                      <div className="kycInfoItem">
                        <div className="kycInfoIcon">
                          <img src={iconNetWorth} alt="" className="kycInfoIconImg" />
                        </div>
                        <div className="kycInfoContent">
                          <div className="kycInfoViewLabel">Net worth</div>
                          <div className="kycInfoViewText">
                            {formatRangeWithCurrency(s?.netWorthRange, s?.netWorthCurrency)}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-12 col-lg-6">
                      <div className="kycInfoItem">
                        <div className="kycInfoIcon">
                          <img src={iconAnnualIncome} alt="" className="kycInfoIconImg" />
                        </div>
                        <div className="kycInfoContent">
                          <div className="kycInfoViewLabel">Annual income</div>
                          <div className="kycInfoViewText">
                            {formatRangeWithCurrency(s?.annualIncomeRange, s?.annualIncomeCurrency)}
                          </div>
                        </div>
                      </div>
                    </div>

                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Row 2: Source of funds | Payment routes */}
          <div className="col-12">
          <div className="row g-3 kycEqualHeightRow">
            <div className="col-12 col-lg-6 d-flex">
              <div className="kycViewTile kycSowSofTile h-100 w-100">
                <div className="kycInfoItem kycInfoItemAlignTop">
                  <div className="kycInfoIconSoWSoF">
                    <Lottie animationData={iconSourceFunds} loop autoplay />
                  </div>

                  <div className="kycInfoContent w-100">
                    <div className="kycInfoViewLabelSoWSoF">Source of funds</div>

                    {sofTypesLabels.length ? (
                      <div className="kycViewPills">
                        {sofTypesLabels.map((item, i) => (
                          <span key={i} className="kycViewPillSoWSoF">
                            {item}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <div className="kycInfoViewText">—</div>
                    )}
                  </div>
                </div>

                {hasSofLoanDetails ? (
                  <div className="kycInlineDetailWrap kycInlineDetailWrapFull">
                    <div className="kycInfoViewLabel kycInlineDetailLabel">
                      Loan details
                    </div>
                    <div className="kycInfoViewTextDetails">
                      {s.sourceOfFundsLoanDetails}
                    </div>
                  </div>
                ) : null}

                {hasSofOtherDetails ? (
                  <div className="kycInlineDetailWrap kycInlineDetailWrapFull">
                    <div className="kycInfoViewLabel kycInlineDetailLabel">
                      Other (details)
                    </div>
                    <div className="kycInfoViewTextDetails">
                      {s.sourceOfFundsOtherDetails}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>

            <div className="col-12 col-lg-6 d-flex">
              <div className="kycViewTile kycSowSofTile h-100 w-100">
                <div className="kycInfoItem kycInfoItemAlignTop">
                  <div className="kycInfoIconSoWSoF">
                    <Lottie animationData={iconPaymentRoutes} loop autoplay />
                  </div>

                  <div className="kycInfoContent w-100">
                    <div className="kycInfoViewLabelSoWSoF">Payment routes</div>

                    {paymentRoutesLabels.length ? (
                      <div className="kycViewPills">
                        {paymentRoutesLabels.map((item, i) => (
                          <span key={i} className="kycViewPillSoWSoF">
                            {item}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <div className="kycInfoViewText">—</div>
                    )}
                  </div>
                </div>

                {hasPaymentRoutesThirdPartyDetails ? (
                  <div className="kycInlineDetailWrap kycInlineDetailWrapFull">
                    <div className="kycInfoViewLabel kycInlineDetailLabel">
                      Third-party details
                    </div>
                    <div className="kycInfoViewTextDetails">
                      {s.paymentRoutesThirdPartyDetails}
                    </div>
                  </div>
                ) : null}

                {hasPaymentRoutesOtherDetails ? (
                  <div className="kycInlineDetailWrap kycInlineDetailWrapFull">
                    <div className="kycInfoViewLabel kycInlineDetailLabel">
                      Other (details)
                    </div>
                    <div className="kycInfoViewTextDetails">
                      {s.paymentRoutesOtherDetails}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
          </div>

          {/* <div className="col-12">
            <div className="kycViewTile">
              <div className="row g-3">
                <div className="col-6">
                  <div className="kycInfoViewLabel">Net worth</div>
                  <div className="kycInfoViewText">
                    {[s?.netWorthRange, s?.netWorthCurrency]
                      .filter(Boolean)
                      .join(" ") || "—"}
                  </div>
                </div>

                <div className="col-6">
                  <div className="kycInfoViewLabel">Annual income</div>
                  <div className="kycInfoViewText">
                    {[s?.annualIncomeRange, s?.annualIncomeCurrency]
                      .filter(Boolean)
                      .join(" ") || "—"}
                  </div>
                </div>
              </div>
            </div>
          </div> */}

          {/* <div className="col-6">
            <Tile
              label="Net worth"
              value={[s?.netWorthRange, s?.netWorthCurrency]
                .filter(Boolean)
                .join(" ")}
            />
          </div>
          <div className="col-6">
            <Tile
              label="Annual income"
              value={[s?.annualIncomeRange, s?.annualIncomeCurrency]
                .filter(Boolean)
                .join(" ")}
            />
          </div> */}

          {/* <div className="col-12">
            <Block
              label="Source of funds (narrative)"
              value={s?.sourceOfFunds}
            />
          </div> */}

          {/* <div className="col-12">
            <Block
              label="Third party details (if any)"
              value={s?.thirdPartyDetails}
            />
          </div> */}
        </div>
      </div>
    </div>
  );
}
